import { singleton } from 'tsyringe';
import { LoggerService } from 'app-core/src/services/logger.service';
import { IRobloxListener } from 'app-core/src/interfaces/ow-wrappers/roblox-listener.interface';
import { RobloxExperienceHistoryService } from 'app-core/src/services/roblox-experience-history/roblox-experience-history.service';
import { kRobloxGameId } from 'app-core/src/constants/constants';
import { OWGamesEvents, OWGamesEventsDelegate } from '../../odk-ts';

const kSupportedGameIds: readonly number[] = [kRobloxGameId];
const kRequiredFeatures: string[] = ['match_info', 'game_info', 'gep_internal'];
const kMatchInfoFeature = 'match_info';
const kMatchInfoGameIdKey = 'game_id';
const kMatchInfoUniverseIdKey = 'universe_id';
const kSourceGameTerminated = 'gameTerminated';
const kSourceExperienceClosed = 'experienceClosed';
type TrackExperienceDurationSource =
  | typeof kSourceGameTerminated
  | typeof kSourceExperienceClosed;

export interface RunningExperience {
  placeId?: string;
  experienceId?: string;
  startTS: number;
}

// -----------------------------------------------------------------------------
@singleton()
export class RobloxListenerService
  implements IRobloxListener, OWGamesEventsDelegate
{
  private readonly logger = LoggerService.getCategory('RobloxListenerService');
  private gameEvents?: OWGamesEvents;
  private runningExperience: RunningExperience | null = null;
  private runningGameId: number = 0;

  // ---------------------------------------------------------------------------
  constructor(
    private readonly experienceHistoryService: RobloxExperienceHistoryService,
  ) {}

  // ---------------------------------------------------------------------------
  public async start(gameId: number): Promise<void> {
    if (!kSupportedGameIds.includes(gameId)) {
      return;
    }

    this.logger.info(`Starting for gameId: ${gameId}`);

    this.runningGameId = gameId;
    this.gameEvents = new OWGamesEvents(this, kRequiredFeatures);
    await this.gameEvents.start();
  }

  // ---------------------------------------------------------------------------
  public stop(): void {
    this.logger.info('Stopping');

    if (this.runningExperience?.experienceId) {
      this.trackExperienceDuration(kSourceGameTerminated);
    }

    this.gameEvents?.stop();
    this.gameEvents = undefined;
    this.runningGameId = 0;
    this.resetRunningExperience();
  }

  // ---------------------------------------------------------------------------
  public isActive(): boolean {
    return this.gameEvents != null;
  }

  // ---------------------------------------------------------------------------
  public isHandlingGame(gameId: number): boolean {
    return this.isActive() && this.runningGameId === gameId;
  }

  // ---------------------------------------------------------------------------
  // OWGamesEventsDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onInfoUpdates(info: any): void {
    if (!info?.match_info) {
      return;
    }

    const universeId = this.tryFindUniverseId(info);
    const gameId = this.tryFindGameId(info);

    // undefined = key not present in this update (not applicable, skip)
    // null      = key present but value is null (explicitly cleared)
    // string    = key present with a value

    if (gameId && gameId !== this.runningExperience?.placeId) {
      this.createRunningExperience(gameId);
    }

    if (universeId && universeId !== this.runningExperience?.experienceId) {
      this.setExperienceId(universeId);
    }

    const wasFullyTracked =
      this.runningExperience?.placeId != null &&
      this.runningExperience?.experienceId != null &&
      universeId == null &&
      gameId == null;

    if (wasFullyTracked && (gameId === null || universeId === null)) {
      this.trackExperienceDuration(kSourceExperienceClosed);
    }
  }

  // ---------------------------------------------------------------------------
  onNewEvents(_e: any): void {
    //
  }

  // ---------------------------------------------------------------------------
  /** Returns the game_id value if found in match_info, null if cleared, or undefined if not present. */
  private tryFindGameId(info: any): string | null | undefined {
    return this.tryFindMatchInfoValue(info, kMatchInfoGameIdKey);
  }

  // ---------------------------------------------------------------------------
  /** Returns the universe_id value if found in match_info, null if cleared, or undefined if not present. */
  private tryFindUniverseId(info: any): string | null | undefined {
    return this.tryFindMatchInfoValue(info, kMatchInfoUniverseIdKey);
  }

  // ---------------------------------------------------------------------------
  private tryFindMatchInfoValue(
    info: any,
    key: string,
  ): string | null | undefined {
    if (!info || typeof info !== 'object') {
      return undefined;
    }

    const matchInfoRaw = info[kMatchInfoFeature];
    if (!matchInfoRaw || typeof matchInfoRaw !== 'object') {
      return undefined;
    }

    const matchInfo = matchInfoRaw as Record<string, unknown>;

    if (!(key in matchInfo)) {
      return undefined;
    }

    const value = matchInfo[key];
    return value != null ? String(value) : null;
  }

  // ---------------------------------------------------------------------------
  private trackExperienceDuration(source: TrackExperienceDurationSource): void {
    if (!this.runningExperience) {
      return;
    }

    const endTS = Date.now();
    const durationInSeconds = this.getDurationInSeconds(endTS);

    if (this.runningExperience.experienceId) {
      this.experienceHistoryService.recordSession(
        this.runningExperience.experienceId,
        this.runningExperience.placeId,
        this.runningExperience.startTS,
        durationInSeconds,
      );
    }

    // TODO: wire up to analytics service when an equivalent track() API exists.
    this.logger.info(
      'Tracked experience duration',
      JSON.stringify({
        event: 'launcher_experience_duration',
        experience_id: this.runningExperience.experienceId,
        place_id: this.runningExperience.placeId,
        duration_seconds: durationInSeconds,
        game_id: this.runningGameId,
        source,
      }),
    );

    this.resetRunningExperience();
  }

  // ---------------------------------------------------------------------------
  private createRunningExperience(placeId: string): void {
    const shouldKeepExperienceId = !this.runningExperience?.placeId;

    this.runningExperience = {
      placeId,
      experienceId: shouldKeepExperienceId
        ? this.runningExperience?.experienceId
        : undefined,
      startTS: Date.now(),
    };

    this.logger.info(
      'Created running experience',
      JSON.stringify(this.runningExperience),
    );
  }

  // ---------------------------------------------------------------------------
  private setExperienceId(experienceId: string): void {
    if (!this.runningExperience) {
      this.runningExperience = {
        experienceId,
        startTS: Date.now(),
      };

      this.logger.info(
        'Updated running experience',
        JSON.stringify(this.runningExperience),
      );
      return;
    }

    this.runningExperience.experienceId = experienceId;
    this.logger.info(
      'Updated running experience',
      JSON.stringify(this.runningExperience),
    );
  }

  // ---------------------------------------------------------------------------
  private getDurationInSeconds(endTS: number): number {
    if (!this.runningExperience) {
      return 0;
    }

    const durationInMs = endTS - this.runningExperience.startTS;
    return Math.max(0, Math.floor(durationInMs / 1000));
  }

  // ---------------------------------------------------------------------------
  private resetRunningExperience(): void {
    this.logger.info('resetRunningExperience');
    this.runningExperience = null;
  }
}
